import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/hooks/useAuth";
import { useReducedMotion } from "@/hooks/useReducedMotion";
import { usePet } from "@/hooks/usePet";
import { Button } from "@/components/ui/button";
import { lovable } from "@/integrations/lovable/index";
import { supabase } from "@/integrations/supabase/client";
import { Mail, Lock, ArrowRight, ArrowLeft } from "lucide-react";
import { useTypedTranslation } from "@/i18n/useTypedTranslation";
import { useLocalizedMeta } from "@/i18n/useLocalizedMeta";
import { localePath } from "@/i18n/localePath";

// Floating particles for auth background
const AuthParticles: React.FC = () => {
  const reduced = useReducedMotion();
  const particles = useMemo(() => {
    if (reduced) return [];
    const emojis = ["🐾", "♡", "✦", "🦴", "💛", "·", "🐕", "⋆"];
    return Array.from({ length: 10 }, (_, i) => ({
      id: i,
      emoji: emojis[i % emojis.length],
      x: 5 + Math.random() * 90,
      y: 5 + Math.random() * 90,
      size: 10 + Math.random() * 18,
      duration: 20 + Math.random() * 25,
      delay: Math.random() * 12,
      opacity: 0.04 + Math.random() * 0.07,
      drift: 25 + Math.random() * 35,
    }));
  }, [reduced]);

  if (reduced) return null;

  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      {particles.map((p) => (
        <motion.span
          key={p.id}
          className="absolute select-none will-change-transform"
          style={{ left: `${p.x}%`, top: `${p.y}%`, fontSize: `${p.size}px`, transform: "translateZ(0)" }}
          initial={{ opacity: 0 }}
          animate={{
            opacity: [0, p.opacity, p.opacity * 0.5, p.opacity, 0],
            y: [0, -p.drift, -p.drift * 0.4, -p.drift * 1.1, 0],
            x: [0, p.drift * 0.3, -p.drift * 0.2, p.drift * 0.15, 0],
          }}
          transition={{ duration: p.duration, delay: p.delay, repeat: Infinity, ease: "easeInOut" }}
        >
          {p.emoji}
        </motion.span>
      ))}
    </div>
  );
};

const AuthPage: React.FC = () => {
  useLocalizedMeta();
  const navigate = useNavigate();
  const { user, signIn, signUp, loading } = useAuth();
  const { pet, loading: petLoading } = usePet();
  const { t, i18n } = useTypedTranslation();
  const [mode, setMode] = useState<'login' | 'signup' | 'reset' | 'update-password'>('login');
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [authLoading, setAuthLoading] = useState(false);
  const [signupSuccess, setSignupSuccess] = useState(false);
  const [selectedMode, setSelectedMode] = useState<"everyday" | "memorial" | "kids" | null>(null);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [focused, setFocused] = useState<string | null>(null);

  // Detect password reset redirect
  React.useEffect(() => {
    const hash = window.location.hash;
    if (hash.includes('type=recovery')) {
      setMode('update-password');
    }
  }, []);

  const lp = (path: string) => localePath(path, i18n.language);

  React.useEffect(() => {
    if (user && !petLoading && pet) navigate(lp("/chat"), { replace: true });
  }, [user, pet, petLoading, navigate]);

  const isLogin = mode === 'login';

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setAuthLoading(true);
    try {
      if (mode === 'reset') {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${window.location.origin}${lp('/auth')}`,
        });
        if (error) throw error;
        setSuccess(t('auth.reset_email_sent'));
        setAuthLoading(false);
        return;
      }
      if (mode === 'update-password') {
        const { error } = await supabase.auth.updateUser({ password });
        if (error) throw error;
        setSuccess(t('auth.password_updated'));
        setMode('login');
        setAuthLoading(false);
        return;
      }
      if (isLogin) {
        await signIn(email, password);
      } else {
        await signUp(email, password);
        setSignupSuccess(true);
        setAuthLoading(false);
        return;
      }
    } catch (err: any) {
      if (mode === 'reset') setError(t('auth.error_reset'));
      else if (mode === 'update-password') setError(t('auth.error_update_password'));
      else setError(err.message || t('auth.error_generic'));
    }
    setAuthLoading(false);
  };

  const handleStart = () => {
    if (selectedMode) navigate(lp("/onboarding"), { state: { mode: selectedMode } });
  };

  const handleGoogleSignIn = async () => {
    setGoogleLoading(true);
    setError("");
    try {
      const { error } = await lovable.auth.signInWithOAuth("google", {
        redirect_uri: window.location.origin,
      });
      if (error) setError(error.message || t('auth.error_google'));
    } catch (err: any) {
      setError(err.message || t('auth.error_google'));
    }
    setGoogleLoading(false);
  };

  if (loading || petLoading) {
    return (
      <div className="flex items-center justify-center min-h-[100dvh] bg-chat-bg">
        <motion.div
          animate={{ scale: [1, 1.3, 1], rotate: [0, 10, -10, 0] }}
          transition={{ duration: 1.8, repeat: Infinity }}
          className="text-5xl"
        >
          🐾
        </motion.div>
      </div>
    );
  }

  const modeItems = [
    { mode: "everyday" as const, emoji: "🐕", title: t('auth.mode_everyday_title'), desc: t('auth.mode_everyday_desc') },
    { mode: "memorial" as const, emoji: "🌈", title: t('auth.mode_memorial_title'), desc: t('auth.mode_memorial_desc') },
    { mode: "kids" as const, emoji: "🐾", title: t('auth.mode_kids_title'), desc: t('auth.mode_kids_desc') },
  ];

  return (
    <div className="min-h-[100dvh] flex flex-col items-center justify-center px-4 relative overflow-hidden bg-chat-bg">
      <AuthParticles />

      {/* Warm glowing orbs */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/3 w-[400px] h-[400px] rounded-full bg-primary/8 blur-[80px] will-change-transform" style={{ transform: "translateZ(0)" }} />
        <div className="absolute bottom-1/4 right-1/4 w-[350px] h-[350px] rounded-full bg-accent/8 blur-[80px] will-change-transform" style={{ transform: "translateZ(0)" }} />
        <div className="absolute top-2/3 left-1/4 w-[300px] h-[300px] rounded-full bg-warm-peach/15 blur-[80px] will-change-transform" style={{ transform: "translateZ(0)" }} />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 30, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.7, ease: [0.34, 1.56, 0.64, 1] }}
        className="text-center max-w-[420px] w-full relative z-10"
      >
        {/* Logo area */}
        <motion.button
          onClick={() => navigate(lp("/"))}
          whileHover={{ scale: 1.15, rotate: 5 }}
          whileTap={{ scale: 0.9 }}
          className="relative inline-block mb-3"
        >
          <motion.div
            animate={{ scale: [1, 1.08, 1] }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
            className="w-20 h-20 rounded-full bg-gradient-to-br from-warm-peach/60 to-warm-orange/30 flex items-center justify-center shadow-[0_8px_32px_-8px_hsl(var(--warm-orange)/0.25)] mx-auto"
          >
            <span className="text-4xl">🐕</span>
          </motion.div>
          <motion.div
            animate={{ scale: [1, 1.5, 1], opacity: [0.3, 0, 0.3] }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
            className="absolute inset-0 rounded-full bg-warm-peach/20"
          />
        </motion.button>

        <motion.h1
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="text-3xl font-extrabold text-foreground mb-1 tracking-tight"
        >
          Peternity
        </motion.h1>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.25 }}
          className="text-muted-foreground text-sm mb-8"
        >
          {t('auth.subtitle')}
        </motion.p>

        <AnimatePresence mode="wait">
          {!user ? (
            <motion.div
              key="auth"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4 }}
            >
              {signupSuccess ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ type: "spring", stiffness: 300, damping: 20 }}
                  className="bg-card/80 backdrop-blur-xl rounded-3xl p-8 shadow-[0_8px_40px_-12px_hsl(var(--primary)/0.15)] border border-warm-peach/15"
                >
                  <motion.div
                    animate={{ y: [0, -5, 0] }}
                    transition={{ duration: 2, repeat: Infinity }}
                    className="text-5xl mb-4"
                  >
                    📧
                  </motion.div>
                  <p className="text-foreground font-bold text-lg mb-2">{t('auth.email_confirm_title')}</p>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {t('auth.email_confirm_text')}
                    <span className="font-semibold text-foreground">{email}</span>
                  </p>
                  <motion.button
                    whileHover={{ x: -3 }}
                    onClick={() => { setSignupSuccess(false); setMode('login'); }}
                    className="text-primary text-sm mt-6 font-semibold hover:underline inline-flex items-center gap-1"
                  >
                    {t('auth.email_confirm_back')}
                  </motion.button>
                </motion.div>
              ) : (
                <motion.form
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.1 }}
                  onSubmit={handleAuth}
                  className="bg-card/70 backdrop-blur-xl rounded-3xl p-7 md:p-8 shadow-[0_8px_40px_-12px_hsl(var(--primary)/0.12)] border border-warm-peach/12 space-y-4"
                >
                  <motion.h2
                    key={mode}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="text-xl font-bold text-foreground"
                  >
                    {mode === 'reset' ? t('auth.reset_title') : mode === 'update-password' ? t('auth.new_password') : isLogin ? t('auth.login_title') : t('auth.signup_title')}
                  </motion.h2>
                  {mode === 'reset' && (
                    <p className="text-sm text-muted-foreground -mt-1">{t('auth.reset_subtitle')}</p>
                  )}

                  {/* Email field */}
                  <div className="relative">
                    <motion.div animate={focused === "email" ? { scale: 1.02 } : { scale: 1 }} transition={{ duration: 0.2 }}>
                      <div className={`relative flex items-center rounded-2xl border-[1.5px] transition-all duration-300 ${
                        focused === "email"
                          ? "border-primary/50 shadow-[0_0_0_4px_hsl(var(--primary)/0.06)]"
                          : "border-warm-peach/20 hover:border-warm-peach/40"
                      }`}>
                        <Mail className="absolute left-4 h-4 w-4 text-muted-foreground/50" />
                        <input
                          type="email"
                          placeholder={t('auth.email_placeholder')}
                          value={email}
                          onChange={e => setEmail(e.target.value)}
                          onFocus={() => setFocused("email")}
                          onBlur={() => setFocused(null)}
                          required
                          className="w-full bg-transparent pl-11 pr-4 py-3.5 text-[15px] font-nunito placeholder:text-muted-foreground/40 focus:outline-none rounded-2xl"
                        />
                      </div>
                    </motion.div>
                  </div>

                  {/* Password field — hide for reset mode */}
                  {mode !== 'reset' && (
                  <div className="relative">
                    <motion.div animate={focused === "password" ? { scale: 1.02 } : { scale: 1 }} transition={{ duration: 0.2 }}>
                      <div className={`relative flex items-center rounded-2xl border-[1.5px] transition-all duration-300 ${
                        focused === "password"
                          ? "border-primary/50 shadow-[0_0_0_4px_hsl(var(--primary)/0.06)]"
                          : "border-warm-peach/20 hover:border-warm-peach/40"
                      }`}>
                        <Lock className="absolute left-4 h-4 w-4 text-muted-foreground/50" />
                        <input
                          type="password"
                          placeholder={mode === 'update-password' ? t('auth.new_password_placeholder') : t('auth.password_placeholder')}
                          value={password}
                          onChange={e => setPassword(e.target.value)}
                          onFocus={() => setFocused("password")}
                          onBlur={() => setFocused(null)}
                          required
                          minLength={6}
                          className="w-full bg-transparent pl-11 pr-4 py-3.5 text-[15px] font-nunito placeholder:text-muted-foreground/40 focus:outline-none rounded-2xl"
                        />
                      </div>
                    </motion.div>
                  </div>
                  )}

                  {/* Success */}
                  <AnimatePresence>
                    {success && (
                      <motion.div
                        initial={{ opacity: 0, y: -8, height: 0 }}
                        animate={{ opacity: 1, y: 0, height: "auto" }}
                        exit={{ opacity: 0, y: -8, height: 0 }}
                        className="bg-green-500/10 border border-green-500/20 rounded-xl px-4 py-2.5"
                      >
                        <p className="text-green-700 text-sm font-medium">{success}</p>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Error */}
                  <AnimatePresence>
                    {error && (
                      <motion.div
                        initial={{ opacity: 0, y: -8, height: 0 }}
                        animate={{ opacity: 1, y: 0, height: "auto" }}
                        exit={{ opacity: 0, y: -8, height: 0 }}
                        className="bg-destructive/10 border border-destructive/20 rounded-xl px-4 py-2.5"
                      >
                        <p className="text-destructive text-sm font-medium">{error}</p>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Submit button */}
                  <motion.div whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.98 }}>
                    <Button
                      type="submit"
                      disabled={authLoading}
                      className="w-full rounded-2xl h-13 font-bold text-base shadow-[0_4px_20px_-4px_hsl(var(--primary)/0.3)] hover:shadow-[0_6px_28px_-4px_hsl(var(--primary)/0.4)] transition-shadow"
                    >
                      {authLoading ? (
                        <motion.span animate={{ rotate: 360 }} transition={{ duration: 0.8, repeat: Infinity, ease: "linear" }}>
                          🐾
                        </motion.span>
                      ) : (
                        <span className="flex items-center gap-2">
                          {mode === 'reset' ? t('auth.reset_button') : mode === 'update-password' ? t('auth.update_password_button') : isLogin ? t('auth.submit_login') : t('auth.submit_signup')}
                          <ArrowRight className="h-4 w-4" />
                        </span>
                      )}
                    </Button>
                  </motion.div>

                  {/* Divider, Google, toggle — only for login/signup modes */}
                  {(mode === 'login' || mode === 'signup') && (
                    <>
                      <div className="flex items-center gap-4 py-1">
                        <div className="flex-1 h-px bg-gradient-to-r from-transparent via-warm-peach/20 to-transparent" />
                        <span className="text-xs text-muted-foreground/50 font-medium">{t('common.or')}</span>
                        <div className="flex-1 h-px bg-gradient-to-r from-transparent via-warm-peach/20 to-transparent" />
                      </div>

                      <motion.div whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.98 }}>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={handleGoogleSignIn}
                          disabled={googleLoading || authLoading}
                          className="w-full rounded-2xl h-13 font-semibold text-base gap-2.5 border-warm-peach/15 hover:bg-warm-peach/8 hover:border-warm-peach/25 transition-all duration-300"
                        >
                          {googleLoading ? (
                            <motion.span animate={{ rotate: 360 }} transition={{ duration: 0.8, repeat: Infinity, ease: "linear" }}>
                              🐾
                            </motion.span>
                          ) : (
                            <>
                              <svg className="w-5 h-5" viewBox="0 0 24 24">
                                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                              </svg>
                              {t('auth.google_signin')}
                            </>
                          )}
                        </Button>
                      </motion.div>

                      <motion.button
                        type="button"
                        whileHover={{ scale: 1.02 }}
                        onClick={() => { setMode(isLogin ? 'signup' : 'login'); setError(""); setSuccess(""); }}
                        className="text-sm text-muted-foreground hover:text-primary transition-colors font-medium pt-1"
                      >
                        {isLogin ? t('auth.toggle_no_account') : t('auth.toggle_has_account')}
                        <span className="text-primary font-semibold">
                          {isLogin ? t('auth.toggle_signup') : t('auth.toggle_login')}
                        </span>
                      </motion.button>

                      {isLogin && (
                        <motion.button
                          type="button"
                          whileHover={{ scale: 1.02 }}
                          onClick={() => { setMode('reset'); setError(""); setSuccess(""); }}
                          className="text-xs text-muted-foreground/60 hover:text-primary transition-colors font-medium"
                        >
                          {t('auth.forgot_password')}
                        </motion.button>
                      )}
                    </>
                  )}

                  {/* Back to login — for reset/update-password modes */}
                  {(mode === 'reset' || mode === 'update-password') && (
                    <motion.button
                      type="button"
                      whileHover={{ x: -3 }}
                      onClick={() => { setMode('login'); setError(""); setSuccess(""); }}
                      className="text-primary text-sm font-semibold hover:underline inline-flex items-center gap-1"
                    >
                      {t('auth.back_to_login')}
                    </motion.button>
                  )}
                </motion.form>
              )}
            </motion.div>
          ) : (
            /* Mode selection */
            <motion.div
              key="mode"
              initial={{ opacity: 0, y: 20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.5, ease: [0.34, 1.56, 0.64, 1] }}
              className="space-y-4"
            >
              <div className="flex items-center justify-between mb-5">
                <motion.button
                  whileHover={{ x: -3 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => navigate(lp("/"))}
                  className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors font-medium"
                >
                  <ArrowLeft className="h-4 w-4" />
                  {t('common.back')}
                </motion.button>
                <p className="text-muted-foreground text-sm font-medium">{t('auth.mode_select_label')}</p>
              </div>
              <div className="grid grid-cols-1 gap-3">
                {modeItems.map((item, i) => (
                  <motion.button
                    key={item.mode}
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 + i * 0.12, type: "spring", stiffness: 300, damping: 20 }}
                    whileHover={{ scale: 1.02, y: -2 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setSelectedMode(item.mode)}
                    className={`p-5 rounded-2xl border-2 text-left transition-all duration-300 backdrop-blur-md ${
                      selectedMode === item.mode
                        ? "border-primary bg-primary/8 shadow-[0_4px_24px_-6px_hsl(var(--primary)/0.2)]"
                        : "border-warm-peach/15 bg-card/50 hover:border-primary/30 hover:shadow-[0_4px_20px_-6px_hsl(var(--primary)/0.1)]"
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <motion.span
                        animate={selectedMode === item.mode ? { scale: [1, 1.15, 1] } : {}}
                        transition={{ duration: 1.5, repeat: Infinity }}
                        className="text-4xl"
                      >
                        {item.emoji}
                      </motion.span>
                      <div>
                        <p className="font-bold text-foreground text-base">{item.title}</p>
                        <p className="text-sm text-muted-foreground mt-0.5">{item.desc}</p>
                      </div>
                    </div>
                  </motion.button>
                ))}
              </div>
              <motion.div whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.98 }}>
                <Button
                  onClick={handleStart}
                  disabled={!selectedMode}
                  size="lg"
                  className="w-full rounded-2xl h-14 font-bold text-base mt-2 shadow-[0_4px_24px_-6px_hsl(var(--primary)/0.25)] hover:shadow-[0_6px_32px_-6px_hsl(var(--primary)/0.35)] transition-shadow"
                >
                  <span className="flex items-center gap-2">
                    {t('common.continue')}
                    <ArrowRight className="h-5 w-5" />
                  </span>
                </Button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Footer tagline */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="text-[11px] text-muted-foreground/40 mt-8"
        >
          {t('auth.footer_tagline')}
        </motion.p>
      </motion.div>
    </div>
  );
};

export default AuthPage;
